import {extractImageDataPromise, createImageData, printImageData, saveImageDataAs, resizeImageData} from 'rc-canvas/lib/Utils';
import {paintActions} from 'rc-canvas/lib/Paint';
import {addParams, rgb2hsv, hsv2rgb, fileNameAndType, getPreferredSettings, maybe} from '../utils'
import {RGB_GREY_VAL, RGB_MAX_VAL, GREY_COLOR, BLUR_FACTOR, Defaults, POPPED} from '../Constants';
import LruMap from 'collections/lru-map';
import {INVERSE} from 'rc-canvas/lib/DrawingModes';

// Normally all the ImageData objects would go into the redux store.
// However, the redux-devtools becomes unusably slow because it tries to display and diff the large ImageData structures.
// Thus, instead of putting these large objects into the redux store, we keep them in a special store called ImageStore
// and we also create a dummy wrapper class ImagesRecord, which has accessors to fetch the image objects from that store.
// Only the ImagesRecord objects are stored in the redux store. This breaks the redux devtools replay feature for images, but
// that's the trade-off until we find a way to make redux-devtools skip these types of large object from its monitors

const ImageStore = {origImageData: null, cleanPoppedImageData: null, cleanEnhImageData: null, enhImageData: null, poppedImageData: null};

export class ImagesRecord {
    constructor({origImageData, cleanPoppedImageData, cleanEnhImageData, enhImageData, poppedImageData} = {}) {
        ImageStore.origImageData = origImageData;
        ImageStore.cleanPoppedImageData = cleanPoppedImageData;
        ImageStore.cleanEnhImageData = cleanEnhImageData;
        ImageStore.enhImageData = enhImageData;
        ImageStore.poppedImageData = poppedImageData;
        this.timestamp = new Date();
    }

    get origImageData() {
        return ImageStore.origImageData;
    }

    get cleanPoppedImageData() {
        return ImageStore.cleanPoppedImageData;
    }

    get cleanEnhImageData() {
        return ImageStore.cleanEnhImageData;
    }

    get enhImageData() {
        return ImageStore.enhImageData;
    }

    get poppedImageData() {
        return ImageStore.poppedImageData;
    }

    isEmpty = () => {
        return !ImageStore.origImageData || !ImageStore.enhImageData || !ImageStore.poppedImageData;
    };
}

let paintCanvasContext;
const getPaintCanvasContext = (width, height) => {
    if (!paintCanvasContext) {
        const paintCanvas = document.createElement('CANVAS');
        paintCanvas.width = width;
        paintCanvas.height = height;
        paintCanvasContext = paintCanvas.getContext('2d');
    } else {
        paintCanvasContext.canvas.width = width;
        paintCanvasContext.canvas.height = height;
    }
    return paintCanvasContext;
};

export const filterApplicableEdits = (editHistory) => {
    return (editHistory || []).filter(historyItem => !historyItem.hidden && !historyItem.deleted && historyItem.historyProps);
};

const applyEditsOnImages = ({origImageData, cleanEnhImageData, cleanPoppedImageData}, editHistory = []) => {
    const applicableEdits = filterApplicableEdits(editHistory);
    if (applicableEdits.length === 0) {
        return {enhImageData: cleanEnhImageData, poppedImageData: cleanPoppedImageData};
    }
    const origData = origImageData.data;
    const enhData = cleanEnhImageData.data.slice();
    const poppedData = cleanPoppedImageData.data.slice();
    const {width, height} = cleanEnhImageData;
    const factor = BLUR_FACTOR * 100 / RGB_MAX_VAL;
    let pixelIterationTime = 0;
    const startTime = performance.now();
    const editCount = applicableEdits.length;
    applicableEdits.forEach((historyItem) => {
        const {historyProps, mode} = historyItem;
        const {transformation} = historyProps;
        const inverseTx = transformation.inverse();

        let colStart = 0;
        let colEnd = width;
        let rowStart = 0;
        let rowEnd = height;
        if (mode !== INVERSE) {
            colStart = Math.max(0, Math.floor(inverseTx.transformX(0)));
            colEnd = Math.min(width, Math.ceil(inverseTx.transformX(historyProps.width)));
            rowStart = Math.max(0, Math.floor(inverseTx.transformY(0)));
            rowEnd = Math.min(height, Math.ceil(inverseTx.transformY(historyProps.height)));
        }

        const paintCanvasCtx = getPaintCanvasContext(width, height);
        paintCanvasCtx.clearRect(0, 0, width, height);
        inverseTx.applyOnContext(paintCanvasCtx);
        paintActions(paintCanvasCtx, historyItem, false, {lassoOutlineColor: GREY_COLOR});

        const paintImage = paintCanvasCtx.getImageData(0, 0, width, height);
        // saveImageDataAs(paintImage, 'paintImage.jpg', 'image/jpeg');
        const paintImageData = paintImage.data;

        const eraseBoostLevel = maybe(historyItem.historyProps.eraseBoostLevel, Defaults.ERASE_BOOST_LEVEL_MIN);
        const hasEraseBoost = eraseBoostLevel > Defaults.ERASE_BOOST_LEVEL_MIN;

        const pixelItStart = performance.now();
        for (let col = colStart; col < colEnd; ++col) {
            for (let row = rowStart; row < rowEnd; ++row) {
                const imgInd = 4 * (col + width * row);

                const paintVal = paintImageData[imgInd + 3];

                if (paintVal === 0) {
                    continue;
                }
                if (!hasEraseBoost || paintVal === RGB_GREY_VAL) {
                    enhData[imgInd] = RGB_GREY_VAL;
                    enhData[imgInd + 1] = RGB_GREY_VAL;
                    enhData[imgInd + 2] = RGB_GREY_VAL;

                    poppedData[imgInd] = origData[imgInd];
                    poppedData[imgInd + 1] = origData[imgInd + 1];
                    poppedData[imgInd + 2] = origData[imgInd + 2];
                } else { // hasBlur || hasEraseBoost
                    const hsv = rgb2hsv(origData[imgInd], origData[imgInd + 1], origData[imgInd + 2]);
                    const eraseValue = -eraseBoostLevel / 100;
                    const currEnhValue = enhData[imgInd];
                    const newEnhValue = Math.min((1 - eraseValue) * (currEnhValue - RGB_GREY_VAL), RGB_GREY_VAL - 1) + RGB_GREY_VAL;
                    hsv[2] += factor * (newEnhValue / RGB_GREY_VAL - 1);
                    const rgb = hsv2rgb(hsv[0], hsv[1], hsv[2]);

                    enhData[imgInd] = newEnhValue;
                    enhData[imgInd + 1] = newEnhValue;
                    enhData[imgInd + 2] = newEnhValue;

                    poppedData[imgInd] = rgb[0];
                    poppedData[imgInd + 1] = rgb[1];
                    poppedData[imgInd + 2] = rgb[2];
                }
            }
        }
        const pixelItEnd = performance.now();
        pixelIterationTime += (pixelItEnd - pixelItStart);
    });
    const enhImageData = createImageData(enhData, width, height);
    const poppedImageData = createImageData(poppedData, width, height);
    const endTime = performance.now();
    console.log('applyEditsOnImages took', Math.round(endTime - startTime), 'millis for', editCount, 'edits.',
        'Iteration time=', Math.round(pixelIterationTime), 'millis',
        'paint time=', Math.round(endTime - startTime - pixelIterationTime), 'millis',
        Math.round(100 * (endTime - startTime - pixelIterationTime) / (endTime - startTime)) + '%');
    return {enhImageData, poppedImageData};
};

export const applyEnhancement = (origImageData, enhImageData) => {
    const startTime = performance.now();
    const {width, height} = origImageData;
    const poppedImageData = createImageData(width, height);
    const origData = origImageData.data;
    const enhData = enhImageData.data;
    const factor = BLUR_FACTOR * 100 / RGB_MAX_VAL;
    const poppedData = poppedImageData.data;
    for (let imgCol = 0; imgCol < width; imgCol += 1) {
        for (let imgRow = 0; imgRow < height; imgRow += 1) {
            const imgInd = 4 * (imgCol + width * imgRow);
            const paintVal = enhData[imgInd];
            if (paintVal === RGB_GREY_VAL) {
                poppedData[imgInd] = origData[imgInd];
                poppedData[imgInd + 1] = origData[imgInd + 1];
                poppedData[imgInd + 2] = origData[imgInd + 2];
                poppedData[imgInd + 3] = RGB_MAX_VAL;
            } else {
                const hsv = rgb2hsv(origData[imgInd], origData[imgInd + 1], origData[imgInd + 2]);

                hsv[2] += factor * (paintVal / RGB_GREY_VAL - 1);
                const rgb = hsv2rgb(hsv[0], hsv[1], hsv[2]);

                poppedData[imgInd] = rgb[0];
                poppedData[imgInd + 1] = rgb[1];
                poppedData[imgInd + 2] = rgb[2];
                poppedData[imgInd + 3] = RGB_MAX_VAL;
            }
        }
    }
    const endTime = performance.now();
    console.log('applyEnhancement took', Math.round(endTime - startTime), 'millis');
    return poppedImageData;
};

export const CANVAS_IMAGES_LOAD_REQUEST = 'CANVAS_IMAGES_LOAD_REQUEST';
export const CANVAS_IMAGES_LOAD_SUCCESS = 'CANVAS_IMAGES_LOAD_SUCCESS';
export const CANVAS_IMAGES_LOAD_FAILURE = 'CANVAS_IMAGES_LOAD_FAILURE';

export const updateImages = (cleanImages, editHistory = []) => {
    const updated = applyEditsOnImages(cleanImages, editHistory);
    const imagesRecord = new ImagesRecord({...cleanImages, ...updated});
    return {
        type: CANVAS_IMAGES_LOAD_SUCCESS,
        imagesRecord
    };
};

const imageDataCache = new LruMap([], 50);

export const cachedExtractImageDataPromise = (src, width, height, type) => {
    const key = `${src}#${type},${width}x${height}`;
    const cachedImgData = imageDataCache.get(key);
    if (cachedImgData) {
        return Promise.resolve(cachedImgData);
    }
    return extractImageDataPromise(src, width, height).then(imgData => {
        imageDataCache.set(key, imgData);
        return imgData;
    });
};

export const loadImagesPromise = ({imageInfo, spread, amplitude, srcPrefix}, editHistory = [], includeFullImage = false) => {
    const {src, poppedImageSrcPrefix, enhancementImageSrcPrefix, width, height, fullWidth, fullHeight} = imageInfo;
    const origImageSrc = srcPrefix + src;
    const poppedImageSrc = addParams(srcPrefix + poppedImageSrcPrefix, {spread, amplitude});
    const enhancementImageSrc = addParams(srcPrefix + enhancementImageSrcPrefix, {spread, amplitude});

    const promises = [
        cachedExtractImageDataPromise(origImageSrc, width, height, 'orig'),
        cachedExtractImageDataPromise(poppedImageSrc, width, height, 'popped'),
        cachedExtractImageDataPromise(enhancementImageSrc, width, height, 'enh'),
    ];

    if (includeFullImage && fullWidth) {
        const {fullSrc} = imageInfo;
        const fullImageSrc = srcPrefix + fullSrc;
        promises.push(cachedExtractImageDataPromise(fullImageSrc, fullWidth, fullHeight, 'full-orig'));
    }

    return Promise.all(promises).then(([origImageData, poppedImageData, enhImageData, fullOrigImageData]) => {
        const cleanPoppedImageData = createImageData(poppedImageData.data, poppedImageData.width, poppedImageData.height);
        const cleanEnhImageData = createImageData(enhImageData.data, enhImageData.width, enhImageData.height);
        const updated = applyEditsOnImages({origImageData, cleanEnhImageData, cleanPoppedImageData}, editHistory);
        let result = {origImageData, cleanPoppedImageData, cleanEnhImageData, ...updated};
        if (fullOrigImageData) {
            const fullEnhImageData = resizeImageData(enhImageData, fullWidth, fullHeight);
            const fullPoppedImageData = applyEnhancement(fullOrigImageData, fullEnhImageData);
            result = {...result, fullOrigImageData, fullEnhImageData, fullPoppedImageData};
        }
        return result;
    });
};

export const loadImages = ({imageInfo, spread, amplitude, srcPrefix}, editHistory = []) => (dispatch) => {
    dispatch({type: CANVAS_IMAGES_LOAD_REQUEST, id: imageInfo.id});
    const promise = loadImagesPromise({imageInfo, spread, amplitude, srcPrefix}, editHistory);
    return promise.then(imageDatas => {
        const imagesRecord = new ImagesRecord(imageDatas);
        return dispatch({type: CANVAS_IMAGES_LOAD_SUCCESS, imagesRecord, id: imageInfo.id});
    }).catch(error => {
        return dispatch({type: CANVAS_IMAGES_LOAD_FAILURE, error, errorNotification: `Failed to load image from ${error}`});
    });
};

export let onResetView;

export const setResetViewAction = (zoomComponent) => {
    onResetView = (e) => {
        zoomComponent.reset(e);
    }
};

const preferredImagePromise = (imageInfo, user, srcPrefix, editHistory = [], includeFullImage = false) => {
    const {preferredAmplitude, preferredSpread} = getPreferredSettings(imageInfo, user);
    return loadImagesPromise({imageInfo, amplitude: preferredAmplitude, spread: preferredSpread, srcPrefix}, editHistory, includeFullImage);
};

export const printImage = (imageInfo, user, srcPrefix, editHistory = []) => {
    preferredImagePromise(imageInfo, user, srcPrefix, editHistory, true).then(({poppedImageData, fullPoppedImageData}) => {
        printImageData(fullPoppedImageData || poppedImageData);
    });
};

export const downloadPoppedImage = (imageInfo, user, srcPrefix, editHistory = [], outFileName = null, outType = null) => {
    const applicableEdits = filterApplicableEdits(editHistory);
    const edited = applicableEdits.length > 0;
    const {preferredAmplitude, preferredSpread} = getPreferredSettings(imageInfo, user);
    const {fileName, mimeType} = fileNameAndType({imageInfo, activeImage: POPPED, amplitude: preferredAmplitude, spread: preferredSpread, edited}, outFileName, outType);
    preferredImagePromise(imageInfo, user, srcPrefix, editHistory, true).then(({poppedImageData, fullPoppedImageData}) => {
        saveImageDataAs(fullPoppedImageData || poppedImageData, fileName, mimeType);
    });
};

export const CANVAS_TRANSFORMATION_CHANGE = 'CANVAS_TRANSFORMATION_CHANGE';

export const onTransformationChange = (transformation) => {
    return {type: CANVAS_TRANSFORMATION_CHANGE, transformation};
};



// WEBPACK FOOTER //
// ./src/actions/EditorCanvasActions.js